<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <b-table
        v-if="visibleFields.length !== 0"
        class="profileTableView"
        primary-key="number"
        show-empty
        responsive
        striped
        hover
        empty-text="Não há plano cadastrado."
        :items="profiles"
        :fields="visibleFields"
        :busy="isLoading"
        :sort-by.sync="sortOptions.sortBy"
        :sort-desc.sync="sortOptions.isSortDirDesc"
        tbody-tr-class="profileTableCustomHeight"
        @row-clicked="onRowSelected"
      >
        <template #table-busy>
          <div class="text-center my-3">
            <b-spinner
              variant="info"
              class="align-middle mb-1"
            />
            <br>
            <strong class="pt-2">Carregando ...</strong>
          </div>
        </template>

        <template #cell(id)="field">
          <b-media vertical-align="center">
            <b-link
              :to="{ name: 'administrator-profiles-edit', params: { id: field.item.id } }"
              class="font-weight-bold d-block text-nowrap text-right"
            >
              #{{ field.item.id }}
            </b-link>
          </b-media>
        </template>

        <template #cell(name)="field">
          <span class="text-nowrap font-weight-bold">
            {{ field.item.name }}
          </span>
        </template>

        <template #cell(situation)="field">
          <span
            v-if="field.item.situation === 'active'"
            class="text-nowrap font-weight-bold"
          >
            <b-badge
              pill
              class="badgeStyle text-capitalize sizeMin d-inline-flex align-items-center justify-content-center"
              :style="{'background-color': '#049F14'}"
            >
              Ativo
            </b-badge>
          </span>
          <span v-else>
            <b-badge
              pill
              class="badgeStyle text-capitalize sizeMin d-inline-flex align-items-center justify-content-center"
              :style="{'background-color': '#FF7E07'}"
            >
              Inativo
            </b-badge>
          </span>
        </template>

        <template #cell(field_actions)="field">
          <div>
            <b-button
              class="btn-icon rounded-circle"
              variant="flat-info"
              size="md"
              @click="showConfirmOrCancelDelete(field.item)"
            >
              <svg-icon
                type="mdi"
                size="18"
                :path="mdiTrashCanOutline"
              />
            </b-button>
          </div>
        </template>
      </b-table>
      <div
        class="mx-2 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-center"
          >
            <b-pagination
              v-if="visibleFields.length !== 0"
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="filter.page_size"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <div>
      <b-button
        id="addProfileBtn"
        v-b-tooltip.hover
        class="btn-icon rounded-circle m-3"
        variant="primary"
        size="md"
        :to="{ name: 'administrator-profiles-create'}"
        title="Adicionar Plano"
      >
        <svg-icon
          type="mdi"
          :path="mdiPlus"
        />
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BRow,
  BCol,
  BTable,
  BPagination,
  VBTooltip,
  BBadge,
  BSpinner,
  BMedia,
  BLink,
} from 'bootstrap-vue'

import {
  mdiPlus,
  mdiTrashCanOutline,
} from '@mdi/js'

import {
  useToast,
} from 'vue-toastification/composition'

import SvgIcon from '@jamescoyle/vue-icon'
import moment from 'moment'
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import syslic from '@/syslic'
import SyncLoad from '@/components/utils/syncLoad'
import MetaData from '@/components/utils/metaData'
import debounce from '@/components/utils/debounce'

export default {
  components: {
    BCard,
    BButton,
    BRow,
    BCol,
    BTable,
    BPagination,
    BBadge,
    BSpinner,
    BMedia,
    BLink,
    SvgIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    active: {
      type: Boolean,
      required: false,
      default: true,
    },
    filter: {
      type: Object,
      required: true,
    },
  },
  emits: ['ready'],

  data() {
    const totalItems = 0
    const currentPage = 1
    const perPage = 10

    const sortOptions = {
      sortBy: 'id',
      isSortDirDesc: false,
    }

    const lastSearch = ''

    const profiles = []

    const tableColumns = [
      {
        key: 'id',
        label: 'Id',
        visible: true,
        fixedColumn: false,
        sortable: true,
        thClass: 'text-nowrap profileTableColumnId',
      },
      {
        key: 'name',
        label: 'Plano',
        visible: true,
        fixedColumn: false,
        sortable: true,
        thClass: 'text-nowrap profileTableColumn',
      },
      {
        key: 'situation',
        label: 'Situação',
        visible: true,
        fixedColumn: false,
        sortable: true,
        thClass: 'text-nowrap profileTableColumn',
      },
      {
        key: 'field_actions',
        label: '',
        visible: true,
        fixedColumn: false,
        sortable: true,
        thClass: 'text-nowrap profileTableColumnId',
      },
    ]

    const fetchId = ''

    const isLoading = true
    const syncLoad = new SyncLoad()
    syncLoad.addTrigger('profiles')

    syncLoad.onReady(() => {
      this.isLoading = false
      this.$emit('ready', true)
    })

    const meta = new MetaData('AccountProfileTable')

    return {
      fetchId,
      totalItems,
      perPage,
      currentPage,
      lastSearch,
      syncLoad,
      profiles,
      tableColumns,
      isLoading,
      sortOptions,
      meta,
    }
  },
  computed: {
    visibleFields() {
      return this.tableColumns.filter(field => field.visible)
    },
  },
  watch: {
    filter: {
      handler: function updateFilter(val) {
        this.currentPage = 1
        if (this.lastSearch !== val.search) {
          this.lastSearch = val.search
        }
        this.fetchData()
      },
      deep: true,
    },
    'sortOptions.sortBy': function updateSortBy() {
      this.currentPage = 1
      this.fetchData()
    },
    'sortOptions.isSortDirDesc': function updateIsSortDirDesc() {
      this.currentPage = 1
      this.fetchData()
    },
    currentPage: function updateCurrentPage() {
      this.fetchData()
    },
  },
  created() {
    this.sortOptions = this.meta.bind(this.sortOptions, 'sortOptions')
    this.fetchData()
  },
  methods: {
    refreshColumns(columns) {
      columns.forEach(element => {
        this.tableColumns.forEach((obj, index) => {
          if (obj.key === element.key) {
            this.tableColumns[index].visible = element.visible
          }
        })
      })
    },
    fetchData() {
      this.isLoading = true
      debounce.call(this.fetch)
    },
    fetch() {
      const fetchPending = Math.random()
      this.fetchId = fetchPending
      const profileFilter = this.filter
      profileFilter.p = this.currentPage

      if (this.sortOptions.isSortDirDesc) {
        profileFilter.ordering = `-${this.sortOptions.sortBy}`
      } else {
        profileFilter.ordering = this.sortOptions.sortBy
      }

      syslic
        .administrator
        .accountProfile
        .fetchAccountProfileList(profileFilter)
        .then(response => {
          let promise = {}

          if (fetchPending === this.fetchId) {
            this.totalItems = response.data.count
            this.profiles = response.data.results
            promise = new Promise(resolve => {
              resolve()
            })
          } else {
            promise = new Promise((resolve, reject) => {
              reject()
            })
          }

          return promise
        })
        .then(() => {
          this.syncLoad.ready('profiles')
        })
        .catch(() => {
          if (fetchPending === this.fetchId) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Falha ao ler os dados dos perfis',
                text: 'Não foi possível ler os dados dos perfis no servidor, por favor entre em contato com o administrador do sistema.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                timeout: 'false',
              },
            })
            this.syncLoad.ready('profiles')
          }
        })
    },
    onRowSelected(item) {
      router.push({
        name: 'administrator-profiles-edit',
        params: {
          id: item.id,
        },
      })
    },
    deleteProfile(item) {
      syslic
        .administrator
        .accountProfile
        .deleteAccountProfile(item.id)
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Plano removido com sucesso.',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
          this.profiles.splice(this.profiles.indexOf(item), 1)
        })
        .catch(error => {
          let textError = 'Não foi possível remover este plano, por favor entre em contato com o administrador do sistema.'

          if ('data' in error) {
            if (error.data.includes('they are referenced through protected foreign keys')) {
              textError = 'Este plano é referenciado, por este motivo não será possível exclui-lo.'
            }
          }

          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Falha ao remover plano.',
              text: textError,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              timeout: false,
            },
          })
        })
    },
    showConfirmOrCancelDelete(item) {
      this.$bvModal
        .msgBoxConfirm('Você realmente quer remover esse plano?', {
          id: 'confirmation_box',
          title: 'Confirmar?',
          size: 'sm',
          okVariant: 'primary',
          okTitle: 'Sim',
          cancelTitle: 'Não',
          cancelVariant: 'outline-primary',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (value) {
            this.deleteProfile(item)
          }
        })
    },
  },
  setup() {
    const toast = useToast()

    return {
      toast,
      moment,
      mdiPlus,
      mdiTrashCanOutline,
    }
  },
}
</script>

<style lang="scss">
.profileTableView {
  border-radius: .4rem;
}

.profileTableColumn {
  min-width: 6rem;
}

.profileTableColumnId {
  width: 2rem;
}

.profileTableCustomHeight {
  height: 4.5rem;
}

.badgeStyle {
  height: 2.2rem;
  min-width: 10rem;
  font-size: 1rem;
}

#addProfileBtn {
  position: fixed;
  right: 0;
  bottom: 0;
}
</style>
